import styled from 'styled-components';

import {NavBar} from 'components/ui-kit/NavBar';
import {FilterContent} from 'components/ui-kit/Table2.0/HeaderWrapper/Filtration/styled';
import {colors} from 'theme';

export const PageContent = styled.div`
    min-height: calc(100vh - 125px);
`;

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;

    button {
        width: auto;
    }
`;

export const NavigationContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    background: ${colors.white};
    padding: 20px;
    border-bottom: 1px solid ${colors.border};

    ${NavBar} {
        flex: 1;
        min-width: 0;
        max-height: none;
        overflow-x: auto;
        justify-content: flex-start;

        a {
            flex-shrink: 0;
            white-space: nowrap;
        }
    }
`;

export const SearchFields = styled.div<{$columns?: number}>`
    display: grid;
    grid-template-columns: ${(p) =>
        p.$columns
            ? `repeat(${p.$columns}, minmax(0, 1fr))`
            : 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.6fr)'};
    gap: 12px;
    width: 856px;
    max-width: 100%;
    min-width: 0;
    margin-right: auto;

    @media (max-width: 768px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const ExportFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const ExportPopoverAnchor = styled.div`
    > button {
        width: auto;
    }
`;

export const ExportFilterContent = styled(FilterContent)`
    width: 440px;
`;

export const EventId = styled.span`
    color: ${colors.brandPrimary};
    word-break: break-all;
`;

export const BreakAllValue = styled.span`
    word-break: break-all;
`;

export const DetailCard = styled.div`
    max-width: 900px;
    padding: 24px;
    border-radius: 8px;
    background: ${colors.white};
`;

export const DetailHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px 16px;
    padding-bottom: 18px;
    border-bottom: 1px solid ${colors.border};

    > :first-child {
        min-width: 0;
        flex: 1 1 12rem;
    }
`;

export const DetailTitle = styled.h1`
    margin: 0 0 5px;
    color: ${colors.darkGrey};
    font-size: 18px;
    line-height: 24px;
`;

export const DetailId = styled.div`
    color: ${colors.textSecondary};
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    overflow-wrap: anywhere;
`;

export const DetailHeaderActions = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 12px;
    max-width: 100%;

    button {
        width: auto;
        flex-shrink: 0;
        white-space: nowrap;
    }
`;

export const EventBadge = styled.span`
    flex-shrink: 1;
    width: fit-content;
    max-width: 100%;
    background: ${colors.brandPrimary};
    color: ${colors.white};
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    overflow-wrap: anywhere;
`;

export const Section = styled.section`
    padding: 20px 0;
    border-bottom: 1px solid ${colors.border};

    &:last-child {
        padding-bottom: 0;
        border-bottom: 0;
    }
`;

export const SectionTitle = styled.h2`
    margin: 0 0 16px;
    color: ${colors.brandPrimary};
    font-size: 13px;
    line-height: 18px;
    text-transform: uppercase;
`;

export const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 24px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const Field = styled.div<{wide?: boolean}>`
    grid-column: ${(p) => (p.wide ? '1 / -1' : 'auto')};
    min-width: 0;
`;

export const GeneralFieldsGrid = styled(FieldsGrid)`
    grid-template-columns: repeat(6, minmax(0, 1fr));

    > ${Field} {
        grid-column: span 2;
    }

    > ${Field}:nth-child(4),
    > ${Field}:nth-child(5) {
        grid-column: span 3;
    }

    > ${Field}:nth-child(6),
    > ${Field}:last-child {
        grid-column: 1 / -1;
    }

    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr);

        > ${Field}:nth-child(n) {
            grid-column: 1;
        }
    }
`;

export const FieldLabel = styled.div`
    margin-bottom: 4px;
    color: ${colors.textSecondary};
    font-size: 12px;
    line-height: 16px;
`;

export const FieldValue = styled.div<{$mono?: boolean}>`
    color: ${colors.darkGrey};
    font-family: ${(p) => (p.$mono ? "'JetBrains Mono', monospace" : 'inherit')};
    font-size: 14px;
    line-height: 20px;
    overflow-wrap: anywhere;
`;

export const CountsTable = styled.div`
    overflow: hidden;
    border: 1px solid ${colors.border};
    border-radius: 8px;
`;

export const CountRow = styled.div<{header?: boolean}>`
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: ${(p) => (p.header ? '10px 16px' : '12px 16px')};
    border-bottom: 1px solid ${colors.border};
    background: ${(p) => (p.header ? colors.background : colors.white)};
    color: ${(p) => (p.header ? colors.textSecondary : colors.darkGrey)};
    font-size: ${(p) => (p.header ? '12px' : '14px')};

    &:last-child {
        border-bottom: 0;
    }

    > :last-child {
        text-align: right;
    }
`;

export const StatusesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(130px, 1fr));
    gap: 10px;
`;

export const StatusItem = styled.div<{color: string}>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border: 1px solid ${(p) => `${p.color}30`};
    border-radius: 8px;
    background: ${(p) => `${p.color}0d`};
    font-size: 13px;

    &:before {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${(p) => p.color};
        content: '';
    }

    > :last-child {
        margin-left: auto;
        color: ${(p) => p.color};
        font-weight: 600;
    }
`;

export const EmptyDetail = styled.div`
    padding: 40px;
    color: ${colors.textSecondary};
    text-align: center;
    background: ${colors.white};
`;
